// Web Push Notification Service
// Uses the native Web Push API for browser notifications

class NotificationService {
  constructor() {
    this.permission = "default";
    this.isSupported = "Notification" in window;
  }

  async init() {
    if (!this.isSupported) {
      console.warn("Notifications not supported in this browser");
      return false;
    }

    this.permission = Notification.permission;

    if (this.permission === "default") {
      const result = await Notification.requestPermission();
      this.permission = result;
    }

    return this.permission === "granted";
  }

  async requestPermission() {
    if (!this.isSupported) return false;
    const result = await Notification.requestPermission();
    this.permission = result;
    return result === "granted";
  }

  show(title, options = {}) {
    if (!this.isSupported || this.permission !== "granted") {
      console.warn("Notification permission not granted");
      return null;
    }

    const defaultOptions = {
      icon: "/logo192.png",
      badge: "/logo192.png",
      vibrate: [200, 100, 200],
      requireInteraction: false,
      silent: false,
      ...options,
    };

    try {
      const notification = new Notification(title, defaultOptions);

      notification.onclick = (event) => {
        event.preventDefault();
        window.focus();
        if (options.onClick) options.onClick(event);
        notification.close();
      };

      // Auto close after 10 seconds if not clicked
      if (!defaultOptions.requireInteraction) {
        setTimeout(() => notification.close(), 10000);
      }

      return notification;
    } catch (e) {
      console.error("Error showing notification:", e);
      return null;
    }
  }

  // Convenience methods for different notification types
  rideUpdate(message, rideId) {
    return this.show("Ride Update", {
      body: message,
      tag: `ride-${rideId}`,
      data: { type: "ride", rideId },
      onClick: () => {
        window.location.href = `/customer?tab=rides`;
      },
    });
  }

  driverAssigned(driverName, rideId) {
    return this.show("Driver Assigned!", {
      body: `${driverName} has been assigned to your ride`,
      tag: `driver-${rideId}`,
      icon: "/driver-icon.png",
      onClick: () => {
        window.location.href = `/customer?tab=rides`;
      },
    });
  }

  fareProposal(proposerName, amount, rideId) {
    return this.show("New Fare Proposal", {
      body: `${proposerName} proposed ₹${amount} for your ride`,
      tag: `fare-${rideId}`,
      requireInteraction: true,
      onClick: () => {
        window.location.href = `/customer?tab=rides`;
      },
    });
  }

  rideReminder(pickupTime, pickupLocation) {
    return this.show("Ride Reminder", {
      body: `Your ride to ${pickupLocation} starts in 30 minutes`,
      tag: "ride-reminder",
      requireInteraction: true,
      onClick: () => {
        window.location.href = `/customer?tab=rides`;
      },
    });
  }

  walletUpdate(message) {
    return this.show("Wallet Update", {
      body: message,
      tag: "wallet",
      onClick: () => {
        window.location.href = `/customer?tab=wallet`;
      },
    });
  }

  newMessage(senderName, preview, rideId) {
    return this.show(`Message from ${senderName}`, {
      body: preview.length > 50 ? preview.substring(0, 50) + "..." : preview,
      tag: `message-${rideId}`,
      onClick: () => {
        window.location.href = `/customer?tab=rides`;
      },
    });
  }

  // Admin/Driver specific notifications
  newRideRequest(pickup, drop) {
    return this.show("New Ride Request!", {
      body: `${pickup} → ${drop}`,
      tag: "new-ride",
      requireInteraction: true,
    });
  }

  emergencyAlert(customerName, location) {
    return this.show("⚠️ EMERGENCY ALERT", {
      body: `${customerName} triggered emergency at ${location}`,
      tag: "emergency",
      requireInteraction: true,
      vibrate: [500, 200, 500, 200, 500],
    });
  }
}

// Singleton instance
const notificationService = new NotificationService();

export default notificationService;
export { NotificationService };
